import { JP_PREFECTURE_MAPPINGS } from 'constants/mappings'; // TODO: eventually migrate this to ts
import {
  VALIDATION_RESULTS,
  ADDRESS_RESULT_STATUS,
  VERIFICATION_STATUS,
  AddressValidationResult,
  ValidationResponseAddress,
} from 'typescript/types/api/address_validation';

// These statuses dictate how we should handle the address validation result on the frontend
export enum VALIDATION_STATUS {
  VERIFIED = 'verified',
  PARTIALLY_VERIFIED = 'partiallyVerified',
  FAILED_VERIFICATION = 'failed',
  NO_ACTION = 'noAction',
}

// statuses where we will just let the user continue with their address as is
const FAILED_VALIDATION_CALL_RESULTS = new Set([
  VALIDATION_RESULTS.VALIDATION_SERVICE_ERROR,
  VALIDATION_RESULTS.UNKNOWN_STATUS,
]);

/**
 * The address validation result from the API can have different statuses that dictate how we should handle it on the frontend
 * this function computes a status that we then translate into what the user will seethe status of the address validation result to determine how we should handle it on the frontend
 */
export const computeAddressValidationResultStatus = (
  addressValidationResult: AddressValidationResult | null,
): VALIDATION_STATUS => {
  if (!addressValidationResult) {
    return VALIDATION_STATUS.NO_ACTION;
  }

  const {
    result: validationResult,
    address_service_statuses: addressServiceStatuses,
  } = addressValidationResult;

  if (!addressServiceStatuses) {
    return VALIDATION_STATUS.NO_ACTION;
  }

  const {
    status: addressResultStatus,
    verification_status: verificationStatus,
  } = addressServiceStatuses;

  // if the address validation call itself failed, we let the user continue with their address as is
  if (FAILED_VALIDATION_CALL_RESULTS.has(validationResult)) {
    return VALIDATION_STATUS.NO_ACTION;
  }

  // https://prototype.sqprod.co/#/docs/squareup.address.service.AddressResult.Status
  switch (addressResultStatus) {
    case ADDRESS_RESULT_STATUS.SUCCESS: {
      // if address is verified, we send the address foward with corrections auto applied for the user
      return VALIDATION_STATUS.VERIFIED;
    }
    case ADDRESS_RESULT_STATUS.FAILED_VERIFICATION: {
      // if the address is partially verified, we show the user the corrected address and ask them to correct or confirm
      if (verificationStatus === VERIFICATION_STATUS.PARTIALLY_VERIFIED) {
        return VALIDATION_STATUS.PARTIALLY_VERIFIED;
      }

      // else, we tell them we can't verify the address, and ask them to make corrections or confirm and continue with their address
      return VALIDATION_STATUS.FAILED_VERIFICATION;
    }
    default: {
      // if the address has any other status, we let them pass with their address as is
      return VALIDATION_STATUS.NO_ACTION;
    }
  }
};

/**
 * @param {string} prefectureKanji - the Kanji characters for the prefecture (administrative_district_level_1)
 * @returns {string} - the ISO code for the prefecture
 */
export const kanjiToISO = (
  prefectureKanji: keyof typeof JP_PREFECTURE_MAPPINGS,
): string => {
  return JP_PREFECTURE_MAPPINGS[prefectureKanji];
};

/**
 * @param {string} prefectureISO - the ISO code for the prefecture
 * @returns {string} - the Kanji characters for the prefecture
 */
export const ISOToKanji = (prefectureISO: string): string => {
  return Object.keys(JP_PREFECTURE_MAPPINGS).find(
    (key) => JP_PREFECTURE_MAPPINGS[key] === prefectureISO,
  );
};

/**
 * address validation response from the API can have null / extra fields
 * this causes weird behavior with react-hook-form
 * @param address - ValidationResponseAddress
 * @returns address with null fields removed
 */
export const cleanAddressValidationResult = (
  address?: ValidationResponseAddress,
) => {
  if (!address) {
    return {};
  }

  return Object.keys(address).reduce((acc, key) => {
    if (address[key] !== null) {
      if (key === 'name') {
        // name is a special case where we just want to remove it if it exists
        // our backend overrides first and last name with the name field making it so
        // the first and last name always get stuck as whatever the first validation result returns
        // see: addresses_controller.rb#L63-L64
        // so we just remove it from the address object

        return acc;
      }

      return { ...acc, [key]: address[key] };
    }

    return acc;
  }, {} as ValidationResponseAddress);
};

// The following are just helper functions to determine the status of the address validation result
// so we don't have to compare the statuses in the components themselves
export const isValidationSuccess = (result: AddressValidationResult) => {
  const status = computeAddressValidationResultStatus(result);
  return status === VALIDATION_STATUS.VERIFIED;
};

export const isValidationFailed = (result: AddressValidationResult) => {
  const status = computeAddressValidationResultStatus(result);
  return status === VALIDATION_STATUS.FAILED_VERIFICATION;
};

export const isValidationPartiallyVerified = (
  result: AddressValidationResult,
) => {
  const status = computeAddressValidationResultStatus(result);
  return status === VALIDATION_STATUS.PARTIALLY_VERIFIED;
};

export const isValidationNoAction = (result: AddressValidationResult) => {
  const status = computeAddressValidationResultStatus(result);
  return status === VALIDATION_STATUS.NO_ACTION;
};
